// Signup.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  User,
  Smartphone,
  Mail,
  Lock,
  ShieldCheck,
  Calendar,
  Venus,
  CheckCircle
} from 'lucide-react';
import AuthLayout from '../helpers/AuthLayout';

const Signup = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    gender: '',
    dob: '',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // ---------------- VALIDATION ----------------
  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'fullName':
        if (!value.trim()) error = 'Full name is required';
        else if (value.trim().length < 2) error = 'Name is too short';
        break;

      case 'email':
        if (!value) error = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          error = 'Invalid email';
        break;

      case 'phone':
        if (!value) error = 'Mobile number is required';
        else if (!/^\d{10}$/.test(value))
          error = 'Enter valid 10-digit number';
        break;

      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 8) error = 'Min 8 characters';
        else if (!/[A-Z]/.test(value)) error = 'One uppercase required';
        else if (!/[a-z]/.test(value)) error = 'One lowercase required';
        else if (!/[0-9]/.test(value)) error = 'One number required';
        else if (!/[!@#$%^&*]/.test(value)) error = 'One special char required';
        break;

      case 'confirmPassword':
        if (!value) error = 'Confirm password';
        else if (value !== formData.password) error = 'Passwords do not match';
        break;

      case 'dob':
        if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value))
          error = 'Invalid date';
        break;

      default:
        break;
    }

    return error;
  };

  // ---------------- HANDLERS ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone' && !/^\d*$/.test(value)) return;

    setFormData(prev => ({
      ...prev,
      [name]: name === 'phone' ? value.slice(0, 10) : value
    }));

    if (touched[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formData).forEach(key => {
      if (key === 'gender' || key === 'dob') return;
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setTouched(
      Object.keys(formData).reduce((a, c) => ({ ...a, [c]: true }), {})
    );

    if (validateForm()) {
      console.log('Submitted:', formData);

      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);

      setFormData({
        fullName: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
        gender: '',
        dob: '',
      });
      setTouched({});
      setErrors({});
    }
  };

  return (
    <AuthLayout>
      {/* Toast */}
      <AnimatePresence>
  {showToast && (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="
        fixed
        top-1/2 left-1/8
        -translate-x-1/2 -translate-y-1/2
        z-[9999]
      "
    >
      <div className="
        flex items-start gap-4
        bg-white/90 backdrop-blur-xl
        border border-green-200
        shadow-2xl
        rounded-2xl
        px-6 py-4
        min-w-[320px]
        max-w-[90vw]
      ">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="text-green-600" size={22} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <p className="font-semibold text-gray-900">
            Account created 🎉
          </p>
          <p className="text-sm text-gray-600 mt-0.5">
            Your account has been successfully registered.
          </p>
        </div>
      </div>
    </motion.div>
  )}
</AnimatePresence>


      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl bg-white/20 backdrop-blur-[25px]
        border border-white/40 rounded-[2rem] p-10 shadow-2xl"
      >
        <h2 className="text-3xl font-bold text-rose-900 text-center mb-8 tracking-widest uppercase">
          Register
        </h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Full Name" name="fullName" icon={<User size={18} />} {...{ formData, errors, touched, handleChange, handleBlur }} />
          <Field label="Email Address" name="email" type="email" icon={<Mail size={18} />} {...{ formData, errors, touched, handleChange, handleBlur }} />
          <Field label="Mobile Number" name="phone" icon={<Smartphone size={18} />} {...{ formData, errors, touched, handleChange, handleBlur }} />
          <Field label="Password" name="password" type="password" icon={<Lock size={18} />} {...{ formData, errors, touched, handleChange, handleBlur }} />
          <Field label="Confirm Password" name="confirmPassword" type="password" icon={<ShieldCheck size={18} />} {...{ formData, errors, touched, handleChange, handleBlur }} />

          {/* Gender */}
          <SelectField label="Gender" name="gender" icon={<Venus size={18} />} {...{ formData, handleChange }} />

          {/* DOB */}
          <DateField label="Date of Birth" name="dob" icon={<Calendar size={18} />} {...{ formData, handleChange, errors, touched }} />

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="md:col-span-2 w-full bg-gradient-to-r from-rose-400 to-rose-600
            text-white font-bold py-4 rounded-xl"
          >
            Create Account
          </motion.button>
        </form>

        <div className="mt-8 text-center text-sm font-semibold text-rose-950">
          Already a Member?
          <Link to="/login" className="ml-1 border-b-2 border-rose-900 font-bold">
            Login
          </Link>
        </div>
      </motion.div>
    </AuthLayout>
  );
};

/* ---------- Reusable Fields ---------- */

const Field = ({ label, name, type = 'text', icon, formData, errors, touched, handleChange, handleBlur }) => (
  <div>
    <label className="text-sm font-medium text-rose-900">
      {label} <span className="text-rose-600">*</span>
    </label>
    <div className="relative border-b-2 border-rose-900/20 py-2">
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleChange}
        onBlur={handleBlur}
        className="w-full bg-transparent outline-none"
      />
      <div className="absolute right-0 top-2 text-rose-900/60">{icon}</div>
    </div>
    {touched[name] && errors[name] && (
      <p className="text-red-600 text-xs mt-1">{errors[name]}</p>
    )}
  </div>
);

const SelectField = ({ label, name, icon, formData, handleChange }) => (
  <div>
    <label className="text-sm font-medium text-rose-900">{label}</label>
    <div className="relative border-b-2 border-rose-900/20 py-2">
      <select
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className="w-full bg-transparent outline-none appearance-none"
      >
        <option value="">Select</option>
        <option>Male</option>
        <option>Female</option>
        <option>Other</option>
      </select>
      <div className="absolute right-0 top-2 text-rose-900/60">{icon}</div>
    </div>
  </div>
);

const DateField = ({ label, name, icon, formData, handleChange, errors, touched }) => (
  <div>
    <label className="text-sm font-medium text-rose-900">{label}</label>
    <div className="relative border-b-2 border-rose-900/20 py-2">
      <input
        type="date"
        name={name}
        value={formData[name]}
        onChange={handleChange}
        className="w-full bg-transparent outline-none
        [&::-webkit-calendar-picker-indicator]:opacity-0"
      />
      <div className="absolute right-0 top-2 text-rose-900/60">{icon}</div>
    </div>
    {touched[name] && errors[name] && (
      <p className="text-red-600 text-xs mt-1">{errors[name]}</p>
    )}
  </div>
);

export default Signup;
